import React, { ChangeEvent, memo, useState } from "react";
import { isNil } from "ramda";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import DateFnsUtils from "@date-io/date-fns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import {
  Grid,
  Box,
  TextField,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import { saveRequest } from "../../../store/cert/actions";
import {
  Cert,
  Data,
  caType,
  reasons,
  ReasonType,
} from "../../../store/cert/types";
import pageStyles from "../../pageStyles";
import { FormikProps, Form, Field, Formik, FastField } from "formik";
import { editSchema, newSchema } from "./certEditForm.schema";
import Autocomplete from "@mui/material/Autocomplete";
import ServerAlert from "../../../common/errors/ServerAlert";
import Confirm from "../../../common/confirm/Confirm";
import FormSubmitButtons from "../../../common/buttons/FormSubmitButtons";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import CustomizedRadio from "../../../common/formElements/RadioButton";
import TextInput from "../../../common/formElements/TextInput";
import CustomizedCheckbox from "../../../common/formElements/Checkbox";
import CertExportUploadBtns from "./CertExportUploadBtns";
import CertIssuerSection from "./CertIssuerSection";
import CertDetailSection from "./CertDetailSection";
import { useToastMessage } from "../../../effects/useToastMessage";

const ROOT = "ROOT";
const INT = "INT";
const CLIENT = "CLIENT";

interface CertEditFormProps {
  certificate?: Data;
  caMap?: caType[];
  isCertNew?: boolean;
}
function CertEditForm({ certificate, caMap, isCertNew }: CertEditFormProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();
  const [serverError, setServerError] = useState("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [value, setValue] = React.useState("");

  const initialValues = {
    id: undefined,
    type: "Cert",
    attributes: {
      Description: "",
      CADescription: "",
      CASerialNumber: "",
      CA: undefined,
      CommonName: "",
    },
  };

  const certTypes = [
    { label: "Root CA", value: ROOT },
    { label: "Intermediate CA", value: INT },
    { label: "Client Cert", value: CLIENT },
  ];

  return (
    <Formik
      validationSchema={isCertNew ? newSchema : editSchema}
      validateOnMount
      initialValues={certificate || initialValues}
      onSubmit={() => setShowConfirm(!isCertNew)}
    >
      {(props: FormikProps<Data>) => {
        const {
          errors,
          isSubmitting,
          values,
          setFieldValue,
          initialValues,
          setSubmitting,
          isValid,
        } = props;

        // Show toast errors
        useToastMessage(isSubmitting, errors);

        const certType = (): string => {
          if (!values.attributes?.CA) return CLIENT;
          if (
            values.attributes.CASerialNumber &&
            values.attributes?.CASerialNumber.length > 0
          )
            return INT;
          return ROOT;
        };
        const handleTypeChange = (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          if (isCertNew) {
            setFieldValue("attributes.CA", event.target.value !== CLIENT);
            setValue((event.target as HTMLInputElement).value);
            if (event.target.value === CLIENT) {
              setFieldValue("attributes.ValidityInYears", "2");
            }
            if (event.target.value === INT) {
              setFieldValue("attributes.ValidityInYears", "5");
            }
            if (event.target.value === ROOT) {
              setFieldValue("attributes.ValidityInYears", "");
            }
          } else {
            event.target.disabled = true;
          }
        };

        const reissueConfirmationHandler = () => {
          const callback = (err: string | null, data: Cert | null) => {
            if (err) {
              console.log(err);
              setServerError(err);
              setSubmitting(false);
            } else {
              history.goBack();
            }
          };
          dispatch(
            saveRequest(
              values,
              isCertNew ? undefined : "?reissue=true",
              callback
            )
          );
          setShowConfirm(false);
          setSubmitting(false);
        };

        return (
          <Form className={classes.formWrapper as string}>
            <SectionWrapper title="Certificate">
              <SectionWrapper title="General Info" isChild>
                <Grid container alignItems="flex-start" spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <FormControl component="fieldset">
                        <RadioGroup
                          name="attributes.CA"
                          value={isCertNew ? value : certType()}
                          onChange={handleTypeChange}
                          row
                        >
                          {certTypes.map((cert, idx) => (
                            <CustomizedRadio
                              key={idx}
                              value={cert.value}
                              label={cert.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>

                      <CertExportUploadBtns
                        isCertNew={isCertNew}
                        value={value}
                        certificate={certificate}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <CustomizedCheckbox
                          checked={values.attributes?.Protected || false}
                          onChange={(event, data: boolean) => {
                            if (!initialValues.attributes.Protected) {
                              setFieldValue("attributes.Protected", data);
                            }
                          }}
                          name="attributes.Protected"
                        />
                      }
                      label="Protected"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextInput
                          fieldname="attributes.Description"
                          InputProps={{ readOnly: !isCertNew }}
                          label={isCertNew ? "Description*" : "Description"}
                        />
                      </Grid>
                      {!isCertNew && (
                        <Grid item xs={12} md={4}>
                          <TextInput
                            fieldname="attributes.SerialNumber"
                            InputProps={{ readOnly: true }}
                            label="Serial Number"
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} md={4}>
                        <TextInput
                          fieldname="attributes.CommonName"
                          label={
                            isCertNew && value === CLIENT
                              ? "Common Name"
                              : "Common Name*"
                          }
                        />
                      </Grid>

                      {!isCertNew && (
                        <Grid item xs={12} md={4}>
                          <LocalizationProvider utils={DateFnsUtils}>
                            <DatePicker
                              fullWidth
                              disableToolbar
                              inputVariant="outlined"
                              format="dd/MM/yyyy"
                              id="rev-date-picker"
                              label="Revocation Date"
                              value={
                                values.attributes?.RevocationDate
                                  ? values.attributes.RevocationDate
                                  : null
                              }
                              onChange={(date: Date | null) => {
                                setFieldValue(
                                  "attributes.RevocationDate",
                                  date
                                );
                                if (
                                  !date &&
                                  !isNil(values.attributes?.RevocationReason)
                                ) {
                                  setFieldValue(
                                    "attributes.RevocationReason",
                                    null
                                  );
                                }
                              }}
                              KeyboardButtonProps={{
                                "aria-label": "change date",
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                      )}
                      {!isCertNew && (
                        <Grid item xs={12} md={4}>
                          {values.attributes?.RevocationDate && (
                            <Field name="attributes.RevocationReason">
                              {() => {
                                return (
                                  <Autocomplete
                                    id="rev-reason-autocomplete"
                                    options={reasons as ReasonType[]}
                                    autoHighlight
                                    getOptionLabel={(option) =>
                                      option.description
                                    }
                                    onChange={(
                                      event: ChangeEvent<{}>,
                                      reason: ReasonType | null
                                    ) => {
                                      setFieldValue(
                                        "attributes.RevocationReason",
                                        reason?.reason
                                      );
                                    }}
                                    renderOption={(props, option) => (
                                      <li {...props}>{option.description}</li>
                                    )}
                                    renderInput={(params) => (
                                      <FastField
                                        as={TextField}
                                        {...params}
                                        label="Revocation Reason"
                                        variant="outlined"
                                        inputProps={{
                                          ...params.inputProps,
                                          autoComplete: "new-password", // disable autocomplete and autofill
                                        }}
                                      />
                                    )}
                                  />
                                );
                              }}
                            </Field>
                          )}
                          {values.attributes?.RevocationDate &&
                            errors?.attributes?.RevocationReason && (
                              <FormHelperText error>
                                {errors?.attributes?.RevocationReason}
                              </FormHelperText>
                            )}
                        </Grid>
                      )}
                      <Grid item xs={12} md={4}></Grid>
                      <Grid item xs={12} md={4}></Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </SectionWrapper>
            </SectionWrapper>

            {/* Issuer section */}
            {(isCertNew ? value !== ROOT : certType() !== ROOT) && (
              <SectionWrapper title="Issuer">
                <CertIssuerSection isCertNew={isCertNew} caMap={caMap} />
              </SectionWrapper>
            )}

            {/* Details section */}
            <SectionWrapper title="Details">
              <CertDetailSection isCertNew={isCertNew} />
            </SectionWrapper>

            <FormSubmitButtons
              isDisabled={!isValid || isSubmitting}
              submitAction={() =>
                isCertNew ? reissueConfirmationHandler() : null
              }
            />
            <ServerAlert serverError={serverError} />

            <Confirm
              isOpen={showConfirm}
              onCancel={() => {
                setShowConfirm(false);
                setSubmitting(false);
              }}
              onConfirm={() => reissueConfirmationHandler()}
              title="Reissue Certificate Warning"
              icon="warning"
              confirmLabel="Reissue Certificate"
              contentText={`You are about to reissue this certificate`}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
export default memo(CertEditForm);
